import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import cn from 'classnames';
import { useCallback, useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSetRecoilState, useRecoilValue } from 'recoil';
import { Reaction } from '@/components/Icons/Reaction';
import { Button } from '@/components/ui/button';
import { MAX_CHARS } from '@/config/constants';
import { AnalyticsEvent, useAnalytics } from '@/hooks/useAnalytics';
import { useSocket } from '@/hooks/useSocket';
import { useWidgetState } from '@/hooks/useWidgetState';
import { chatModalState, isMobileState, isWidgetExpandedState, } from '@/state/session';
export const Survey = () => {
    const [activeButton, setActiveButton] = useState(null);
    const isMobile = useRecoilValue(isMobileState);
    const isExpanded = useRecoilValue(isWidgetExpandedState);
    const { trackEvent } = useAnalytics();
    const { t } = useTranslation();
    const [isLoading, setIsLoading] = useState(false);
    const { sendRating, closeSession } = useSocket();
    const setChatModal = useSetRecoilState(chatModalState);
    const inputRef = useRef(null);
    const inputContainerRef = useRef(null);
    const [input, setInput] = useState('');
    const { closeWidget } = useWidgetState();
    const handleClose = useCallback(async () => {
        // Track review popover interaction on close
        setChatModal(null);
        closeSession();
        closeWidget();
    }, [closeSession, closeWidget, setChatModal]);
    useEffect(() => {
        if (inputRef.current) {
            setTimeout(() => {
                var _a;
                (_a = inputRef === null || inputRef === void 0 ? void 0 : inputRef.current) === null || _a === void 0 ? void 0 : _a.focus();
            }, 300);
        }
    }, [input]);
    const handleLimitInputChange = useCallback(() => {
        var _a, _b, _c;
        if (((_a = inputRef.current) === null || _a === void 0 ? void 0 : _a.textContent) &&
            ((_b = inputRef.current) === null || _b === void 0 ? void 0 : _b.textContent.length) >= MAX_CHARS) {
            inputRef.current.textContent = (_c = inputRef.current) === null || _c === void 0 ? void 0 : _c.textContent.slice(0, MAX_CHARS);
            const selection = window.getSelection();
            const range = document.createRange();
            selection === null || selection === void 0 ? void 0 : selection.removeAllRanges();
            range.selectNodeContents(inputRef.current);
            range.collapse(false);
            selection === null || selection === void 0 ? void 0 : selection.addRange(range);
            inputRef.current.focus();
        }
        const element = inputRef === null || inputRef === void 0 ? void 0 : inputRef.current;
        if (!(element === null || element === void 0 ? void 0 : element.textContent)) {
            const breakTag = element === null || element === void 0 ? void 0 : element.getElementsByTagName('br')[0];
            if (breakTag) {
                element === null || element === void 0 ? void 0 : element.removeChild(breakTag);
            }
        }
    }, []);
    const handleInputChange = useCallback(() => {
        var _a;
        handleLimitInputChange();
        setInput(((_a = inputRef.current) === null || _a === void 0 ? void 0 : _a.textContent) || '');
    }, [handleLimitInputChange]);
    const handleSubmit = useCallback(() => {
        if (activeButton !== null) {
            trackEvent(AnalyticsEvent.RatingSubmitted);
            setIsLoading(true);
            sendRating({ rating: activeButton + 1, feedback: input });
        }
    }, [activeButton, input, sendRating, trackEvent]);
    const handleRatingClick = (index) => {
        // Track review popover interaction when a rating is clicked
        trackEvent(AnalyticsEvent.RatingClicked, { rating: index + 1 });
        setActiveButton(index);
    };
    const handleSkipAndClose = useCallback(() => {
        trackEvent(AnalyticsEvent.PopoverReviewSkipAndCloseClicked);
        handleClose();
    }, [handleClose, trackEvent]);
    return (_jsxs("div", { className: "flex h-full flex-col items-center text-center", children: [_jsx("p", { className: "text-text-secondary-foreground mt-4 text-center text-lg leading-7 font-semibold", children: t('survey.title') }), _jsx("p", { className: "text-foreground-muted mx-auto text-sm", children: t('survey.subtitle') }), _jsx("div", { className: "group mx-5 mt-3 mb-4 grid h-9 flex-1 grid-cols-5 gap-4 p-0", children: Array(5)
                    .fill('')
                    .map((_, index) => (_jsx("button", { className: "flex cursor-pointer justify-center transition-all duration-300 hover:scale-110", onClick: () => handleRatingClick(index), children: _jsx(Reaction, { index: index, active: activeButton }) }, index))) }), _jsx("hr", { className: "dark:border-input w-full border-gray-200" }), _jsx("div", { ref: inputContainerRef, className: cn('flex h-[72px] w-[328px] flex-grow flex-col-reverse', {
                    'w-sm': isExpanded,
                }), children: _jsx("div", { ref: inputRef, className: cn('text-foreground mb-6 h-[72px] w-full appearance-none self-start overflow-y-auto text-left text-base break-words will-change-transform', {
                        'text-base': isMobile,
                    }), "aria-label": "input", role: "textbox", tabIndex: 0, "data-placeholder": t('survey.input_placeholder'), contentEditable: true, onInput: handleInputChange }) }), _jsxs("div", { className: "mb-2 flex flex-col items-center justify-center gap-2", children: [_jsx(Button, { disabled: activeButton === null, onClick: handleSubmit, className: cn('w-[328px]', {
                            'w-sm': isExpanded,
                        }), isLoading: isLoading, children: t('buttons.submit') }), _jsx(Button, { onClick: handleSkipAndClose, variant: "ghost-neutral", className: cn('w-[328px]', {
                            'w-sm': isExpanded,
                        }), children: t('buttons.skip') })] })] }));
};
